import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Grid,
  IconButton,
  InputAdornment,
  MenuItem,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import { ContentCopy as ContentCopyIcon } from '@mui/icons-material';
import { LICENCE_STATUSES, formatPlanRange } from './licenceLabels';

const emptyForm = {
  product_id: '',
  plan_id: '',
  expires_at: '',
  max_activations: '1',
  custom_features: {},
  customer_name: '',
  customer_email: '',
  customer_company: '',
  customer_phone: '',
  status: 'active',
  admin_notes: '',
};

function addMonths(date, months) {
  const next = new Date(date);
  const day = next.getDate();
  next.setMonth(next.getMonth() + months);
  if (next.getDate() < day) {
    next.setDate(0);
  }
  return next;
}

function toDateInput(value) {
  if (!value) return '';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return '';
  const pad = (part) => String(part).padStart(2, '0');
  return `${parsed.getFullYear()}-${pad(parsed.getMonth() + 1)}-${pad(parsed.getDate())}`;
}

function featuresFromPlan(plan, catalog) {
  const next = {};
  catalog.forEach((item) => {
    next[item.key] = plan?.features?.[item.key] === true;
  });
  return next;
}

function durationKey(plan) {
  if (!plan || plan.duration_months == null || plan.duration_months === '') {
    return 'none';
  }
  return String(plan.duration_months);
}

function durationChoices(plans) {
  const choices = [];
  const seen = new Set();
  plans.forEach((plan) => {
    const key = durationKey(plan);
    if (seen.has(key)) return;
    seen.add(key);
    choices.push(plan);
  });
  return choices;
}

function planLimits(plan) {
  if (!plan) {
    return { expires_at: '', max_activations: '' };
  }
  const expiresAt = plan.duration_months
    ? toDateInput(addMonths(new Date(), plan.duration_months))
    : '';
  return {
    expires_at: expiresAt,
    max_activations: plan.max_activations ?? '',
  };
}

const LicenceFormDialog = ({
  open,
  mode,
  licence,
  products,
  onClose,
  onSubmit,
}) => {
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!open) return;
    if (mode === 'edit' && licence) {
      const product = products.find((item) => item.id === licence.product_id);
      const catalog = product?.feature_catalog || licence.feature_catalog || [];
      const plan = product?.plans?.find((item) => item.id === licence.plan_id);
      const source = licence.custom_features || plan?.features || {};
      const customFeatures = {};
      catalog.forEach((item) => {
        customFeatures[item.key] = source[item.key] === true;
      });
      setForm({
        product_id: licence.product_id,
        plan_id: licence.plan_id,
        expires_at: toDateInput(licence.expires_at),
        max_activations: licence.max_activations ?? '',
        custom_features: customFeatures,
        customer_name: licence.customer_name || '',
        customer_email: licence.customer_email || '',
        customer_company: licence.customer_company || '',
        customer_phone: licence.customer_phone || '',
        status: licence.status || 'active',
        admin_notes: licence.admin_notes || '',
      });
    } else {
      setForm(emptyForm);
    }
    setError('');
    setSaving(false);
  }, [open, mode, licence, products]);

  const selectedProduct = useMemo(
    () => products.find((product) => String(product.id) === String(form.product_id)),
    [products, form.product_id]
  );
  const catalog = selectedProduct?.feature_catalog || [];
  const plans = selectedProduct?.plans || [];
  const selectedPlan = plans.find((plan) => String(plan.id) === String(form.plan_id));

  const updateField = (name, value) => {
    setForm((current) => ({ ...current, [name]: value }));
  };

  const handleProductChange = (productId) => {
    setForm((current) => ({
      ...current,
      product_id: productId,
      plan_id: '',
      custom_features: {},
      expires_at: '',
    }));
  };

  const handleDurationChange = (key) => {
    const plan = plans.find((item) => durationKey(item) === key);
    if (!plan) return;
    setForm((current) => ({
      ...current,
      plan_id: plan.id,
      custom_features: featuresFromPlan(plan, catalog),
      expires_at: planLimits(plan).expires_at,
    }));
  };

  const handleFeatureToggle = (key, checked) => {
    setForm((current) => ({
      ...current,
      custom_features: { ...current.custom_features, [key]: checked },
    }));
  };

  const handleCopyKey = async () => {
    if (!licence?.license_key) return;
    try {
      await navigator.clipboard.writeText(licence.license_key);
    } catch (copyError) {
      setError('Δεν ήταν δυνατή η αντιγραφή του κλειδιού');
    }
  };

  const handleSubmit = async () => {
    if (!form.product_id || !form.plan_id) {
      setError('Διάλεξε plugin και πλάνο');
      return;
    }
    const seats = parseInt(form.max_activations, 10);
    if (!seats || seats < 1) {
      setError('Οι θέσεις πρέπει να είναι τουλάχιστον 1');
      return;
    }
    setSaving(true);
    setError('');
    const result = await onSubmit({
      product_id: parseInt(form.product_id, 10),
      plan_id: parseInt(form.plan_id, 10),
      expires_at: form.expires_at || '',
      max_activations: seats,
      custom_features: form.custom_features,
      customer_name: form.customer_name,
      customer_email: form.customer_email,
      customer_company: form.customer_company,
      customer_phone: form.customer_phone,
      status: form.status,
      admin_notes: form.admin_notes,
    });
    setSaving(false);
    if (!result?.success) {
      setError(result?.error || 'Αποτυχία αποθήκευσης');
    }
  };

  const planHint = selectedPlan
    ? (form.expires_at ? `Λήξη ${form.expires_at.split('-').reverse().join('/')}` : 'Απεριόριστο')
    : 'Διάλεξε πλάνο για να οριστεί η λήξη.';

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} fullWidth maxWidth="md">
      <DialogTitle>{mode === 'edit' ? 'Επεξεργασία κλειδιού' : 'Νέο κλειδί'}</DialogTitle>
      <DialogContent>
        <Grid container spacing={2} sx={{ mt: 0.5 }}>
          {mode === 'edit' && licence?.license_key && (
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Κλειδί"
                value={licence.license_key}
                InputProps={{
                  readOnly: true,
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton onClick={handleCopyKey} edge="end" title="Αντιγραφή">
                        <ContentCopyIcon fontSize="small" />
                      </IconButton>
                    </InputAdornment>
                  ),
                  sx: { fontFamily: 'monospace' },
                }}
              />
            </Grid>
          )}
          <Grid item xs={12} sm={6}>
            <TextField
              select
              fullWidth
              required
              label="Plugin"
              value={form.product_id}
              onChange={(event) => handleProductChange(event.target.value)}
            >
              {products.map((product) => (
                <MenuItem key={product.id} value={product.id}>
                  {product.plugin_name}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              select
              fullWidth
              required
              label="Πλάνο"
              value={selectedPlan ? durationKey(selectedPlan) : ''}
              onChange={(event) => handleDurationChange(event.target.value)}
              disabled={!selectedProduct}
              helperText={planHint}
            >
              {durationChoices(plans).map((plan) => (
                <MenuItem key={durationKey(plan)} value={durationKey(plan)}>
                  {formatPlanRange(plan.duration_months)}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              type="date"
              label="Λήξη"
              value={form.expires_at}
              onChange={(event) => updateField('expires_at', event.target.value)}
              InputLabelProps={{ shrink: true }}
              helperText="Αν την αλλάξεις, μένει η δική σου ημερομηνία"
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              required
              type="number"
              label="Θέσεις (domains)"
              value={form.max_activations}
              onChange={(event) => updateField('max_activations', event.target.value)}
              inputProps={{ min: 1, max: 1000 }}
            />
          </Grid>
          {catalog.length > 0 && (
            <Grid item xs={12}>
              <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                Διακόπτες
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {catalog.map((item) => (
                  <FormControlLabel
                    key={item.key}
                    control={(
                      <Switch
                        checked={form.custom_features?.[item.key] === true}
                        onChange={(event) => handleFeatureToggle(item.key, event.target.checked)}
                      />
                    )}
                    label={item.label || item.key}
                  />
                ))}
              </Box>
            </Grid>
          )}
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Πελάτης"
              value={form.customer_name}
              onChange={(event) => updateField('customer_name', event.target.value)}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Email"
              value={form.customer_email}
              onChange={(event) => updateField('customer_email', event.target.value)}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Εταιρεία"
              value={form.customer_company}
              onChange={(event) => updateField('customer_company', event.target.value)}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Τηλέφωνο"
              value={form.customer_phone}
              onChange={(event) => updateField('customer_phone', event.target.value)}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              select
              fullWidth
              label="Κατάσταση"
              value={form.status}
              onChange={(event) => updateField('status', event.target.value)}
            >
              {LICENCE_STATUSES.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              multiline
              minRows={2}
              label="Σημειώσεις"
              value={form.admin_notes}
              onChange={(event) => updateField('admin_notes', event.target.value)}
            />
          </Grid>
          {error && (
            <Grid item xs={12}>
              <Typography color="error" variant="body2">{error}</Typography>
            </Grid>
          )}
        </Grid>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={saving}>Ακύρωση</Button>
        <Button variant="contained" onClick={handleSubmit} disabled={saving}>
          {saving ? 'Αποθήκευση...' : 'Αποθήκευση'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default LicenceFormDialog;
